/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 * @author Tomas Burian (burian@cadwork.cz)
 *
 */

import Canvas2DRenderGraph from "App/render/canvas/render_graph";
import Canvas2DContext from "App/canvas_2d_context";
import {mat4, vec3, vec4} from "gl-matrix";
import Factory from "App/render/canvas/nodes/bins/factory";
import {FrustumVolume} from "App/utils/view_frustum";
import {project} from "App/utils/glu";
import {EEmbededElementTypes} from "App/model/elements/factory";
import View from "App/view/view";
import IScene from "js_interface/src/scene_interface";

export default
class Text2d {
    private _sceneAPI: IScene;
    private _renderGraph: Canvas2DRenderGraph;
    private _context: Canvas2DContext;

    private _shapes: any[] = [];
    private _visibleElements: any[] = [];
    private _highlighted: number | null = null;
    private maxRenderTime: number = 0;
    private _lastView: View = null;
    private _weightedDistances: number[] = [1, 1, 3, 6, 9]; // index 0 is not used
    private _geoEntitiesfilter: EEmbededElementTypes[] = [EEmbededElementTypes.TOWNENTITY,
        EEmbededElementTypes.WATERENTITY,
        EEmbededElementTypes.MOUNTAINENTITY];

    private _pgc: any = {
        windowPoint: vec4.create()
    };

    public static TypeName: string = "Text2d";

    constructor(aSceneAPI: IScene, aRenderGraph: Canvas2DRenderGraph) {
        this._sceneAPI = aSceneAPI;
        this._renderGraph = aRenderGraph;
        this._context = <Canvas2DContext> aRenderGraph.getContext();
    }

    public static create(aSceneAPI: IScene, aRenderGraph: Canvas2DRenderGraph): Text2d {
        return new Text2d(aSceneAPI, aRenderGraph);
    }

    public setGeoEntitiesFilter(aFilter: EEmbededElementTypes[]) {
        this._geoEntitiesfilter = aFilter;
        this._sceneAPI.viewModeModule.scheduleRedraw();
    }

    public isEmpty(): boolean {
        return this._shapes.length === 0;
    }

    public addShape(aShape: any): boolean {
        this._shapes.push(aShape);
        aShape.attachToRenderGraphObject(this);

        return true;
    }

    public removeShape(aShape: any) {
        for (let i = 0; i < this._shapes.length; i++) {
            if (this._shapes[i].getUID() === aShape.getUID()) {
                this._shapes.splice(i, 1);
                aShape.detachFromRenderGraphObject();
                return true;
            }
        }

        return false;
    }

    public notifyMeshCreated(aShape: any) {
        this._shapes.push(aShape);
    }

    public getVisibleElements(): any[] {
        return this._visibleElements;
    }

    public setNeedUpdate(Updates: any[]) {
    }

    public highlight(aEntityIndex: number) {
        if (this._visibleElements[this._highlighted] === undefined) {
            return;
        }

        if (this._highlighted !== aEntityIndex) {
            if (this._highlighted !== null) {
                this._visibleElements[this._highlighted].highlight(false);
            }
            this._highlighted = aEntityIndex;
            this._visibleElements[aEntityIndex].highlight(true);
            this.forceRender();
        }
    }

    public highlightNone() {
        if (this._visibleElements[this._highlighted] === undefined) {
            return;
        }

        if (this._highlighted !== null) {
            if (this._highlighted !== null) {
                this._visibleElements[this._highlighted].highlight(false);
            }
            this.forceRender();
            this._highlighted = null;
        }
    }

    public click() {
        if (this._highlighted !== null) {
            let title = this._visibleElements[this._highlighted].getTitle();
            window.open("https://www.google.com/search?q=" + title, '_blank');
        }
    }

    public render(aView: View) {
        //let currentTime = new Date().getTime();
        this._lastView = aView;
        let camera = aView.getCamera();
        let mvp = camera.getViewProjectionMatrix();

        let sceneBBox = this._sceneAPI.getDocument().getSBoundingBox();

        // sort shapes based on distance from camera
        let cameraPosition = camera.getCameraPosition();

        // cache computed distances first
        for (let s = 0; s < this._shapes.length; s++) {
            let positions = this._shapes[s].getGeometry().getPositions();
            let pos = vec3.fromValues(positions[0], positions[1], positions[2]);
            let dist = vec3.distance(cameraPosition, pos);
            let sae = this._shapes[s].getElement().getSceneAPIElementReference();
            sae._properties.computedDistance = dist; // * sae._properties.visibilityImportance; // no visibilityImportance here 'cause it makes distance sorting wrong
        }

        // distance sort
        this._shapes = this._shapes.sort(function(a, b) {
            let saeA = a.getElement().getSceneAPIElementReference();
            let saeB = b.getElement().getSceneAPIElementReference();
            return saeA._properties.computedDistance < saeB._properties.computedDistance ? -1 : 1;
        });

        this._visibleElements = [];

        for (let s = 0; s < this._shapes.length; s++) {

            let shape = this._shapes[s];
            let sapiElement = shape.getElement().getSceneAPIElementReference();

            if (this._geoEntitiesfilter.indexOf(sapiElement.getTypeName()) === -1) { continue; } // apply filter

            // transform the shape's position to screen and fill text on that position
            let positions = shape.getGeometry().getPositions();
            let position = vec3.fromValues(positions[0], positions[1], positions[2]);

            let dist = sapiElement._properties.computedDistance * this._weightedDistances[sapiElement._properties.visibilityImportance];
            let distFactor = 1.0 - (dist / sceneBBox.getRadius());
            // console.log(distFactor);

            // transform the position by manipulation matrix
            let manipulatorTransformation: mat4 = this._sceneAPI.activityModule.getCurrentManipulatorTransformation();
            if (manipulatorTransformation) {
                vec3.transformMat4(position, position, manipulatorTransformation);
            }

            // solve only close texts todo: limit of how many items will be shown
            if (distFactor > 0.01) {
                // test if the 3d point is in a camera positive half space (not behind the camera plane)
                let visible = FrustumVolume.containsPoint(mvp, position);
                if (visible) {

                    let windowPoint = this._pgc.windowPoint;
                    let pos = aView.getPosition();
                    let size = aView.getSize();
                    project(windowPoint, position, aView.getCamera().getViewProjectionMatrix(), [pos[0], pos[1], size[0], size[1]]);

                    windowPoint[0] = Math.round(windowPoint[0]);
                    windowPoint[1] = Math.round(windowPoint[1]);
                    windowPoint[1] = size[1] - 1 - windowPoint[1];

                    sapiElement.renderTest(this._context.getRenderingContext(), this._visibleElements, windowPoint, distFactor);
                }
            }
        }

        // debug
        // console.log('visible ' + this._visibleElements.length);
        for (let v = this._visibleElements.length - 1; v >= 0; v--) {
            let ele = this._visibleElements[v];
            ele.render(this._context.getRenderingContext());
            // this._context.ctx2d.strokeStyle = 'rgb(255, 0, 0)';
            // this._context.ctx2d.strokeRect(ele._bbox.x, ele._bbox.y, ele._bbox.w, ele._bbox.h);
        }

        // 2D render time
        // let deltaTime = new Date().getTime() - currentTime;
        // this.maxRenderTime =  this.maxRenderTime < deltaTime ? deltaTime : this.maxRenderTime;
        // logInfo('2D render time: ' + deltaTime + ' | ' + this.maxRenderTime );
    }

    // TB note:
    // Now when we do highlight we re-render the whole 2D canvas.
    // It could be possible to re-render only small parts of it (entity 2d bounding boxes) to increase performance
    public forceRender() {
        let position = this._lastView.getPosition();
        let size = this._lastView.getSize();
        this._context.getRenderingContext().clearRect(position[0], position[1], size[0], size[1]);
        this.render(this._lastView);
    }
}

Factory.register(Text2d);